import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { templateAccountLoginSecurity } from '../view/template';
import { AccountLoginSecurityWebComponent } from '../component/AccountLoginSecurityWebComponent';
import { AccountLoginSecurityWebView } from '../view/AccountLoginSecurityWebView';
import { DropdownEvents } from '../../../../ui/gzDropdown/types';
import { AccountLoginSecurityData, AccountLoginSecurityEvents } from '../types';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { showToast } from '../../../helpers/showToast';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { translateFn } from '../../../../utils/translator.helper';

export class AccountLoginSecurityWebController extends BaseWebComponentController {
  public component: AccountLoginSecurityWebComponent;
  public view: AccountLoginSecurityWebView;
  public viewConstructor: typeof AccountLoginSecurityWebView;

  constructor(component: AccountLoginSecurityWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: AccountLoginSecurityWebView,
      viewTemplate: templateAccountLoginSecurity,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$authenticationMethodDropdown,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.handleAuthenticationMethodChanged,
      },
      {
        element: this.view.dom.$revokeButton,
        event: 'click',
        callback: this.onRevokeButtonClick,
      },
      {
        element: this.view.dom.$resetButton,
        event: 'click',
        callback: this.onResetButtonClick,
      },
    ];
  }

  private handleAuthenticationMethodChanged(event: WebComponentEvent): void {
    if (!event.detail.newValue) {
      return;
    }

    this.view.changeFormFieldsVisibilityDependingOnAuthenticationMethod(event.detail.newValue.value);
    this.component.dispatchEvent(new CustomEvent(AccountLoginSecurityEvents.AUTHENTICATION_METHOD_CHANGED, event));
  }

  private onRevokeButtonClick(): void {
    const password = this.view.dom.$passwordFormField.value as string;
    if (password.length === 0) {
      this.view.showNoPasswordSpecifiedMessage(
        translateFn('Failed to revoke all trusted browsers.'),
        translateFn(
          'Could not revoke trust for all browsers associated with the GravityZone account. Please specify a password.'
        )
      );
      return;
    }
    this.showRevokeAllBrowsersConfirmationWindow(this.view.userId, password);
  }

  private showRevokeAllBrowsersConfirmationWindow(userId: string, password: string): void {
    const windowBodyHtml = `
      <p>
        ${translateFn('Are you sure you want revoke all browsers?')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('Revoke')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Revoke all browsers?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          await this.dependencies.api.clients.TrustedDevices.forgetAllDevicesForUser(userId, password).toPromise();
          dialog.close();
          showToast(ToastSeverity.SUCCESS, translateFn('All browsers are successfully revoked'));
        } catch (error) {
          dialog.close();
          showToast(ToastSeverity.ERROR, translateFn('Authentication failed. Wrong password.'));
          return false;
        }
      },
      null,
      false
    );
  }

  private async onResetButtonClick(): Promise<void> {
    const password = this.view.dom.$passwordFormField.value as string;
    if (password.length === 0) {
      this.view.showNoPasswordSpecifiedMessage(
        translateFn('Failed to reset 2FA.'),
        translateFn('No password provided. Please specify a password.')
      );
      return;
    }
    this.showReset2FAConfirmationWindow(this.view.userId, password);
  }

  private showReset2FAConfirmationWindow(userId: string, password: string): void {
    const windowBodyHtml = `
      <p>
        ${translateFn('Are you sure you want to reset two factor authentication?')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('Reset')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Reset 2FA?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        try {
          await this.dependencies.api.clients.SecurityManager.disableTwoFactorForUser(userId, password).toPromise();
          dialog.close();
          showToast(ToastSeverity.SUCCESS, translateFn('Two factor authentication successfully reset'));
          this.view.dom.$resetButton.dispatchEvent(
            new CustomEvent(AccountLoginSecurityEvents.TWO_FA_RESET, {
              bubbles: true,
              composed: true,
            })
          );
          this.view.twoFactorAuthentication = false;
          this.view.showFieldsDependingOnTwoFA();
        } catch (error) {
          dialog.close();
          showToast(ToastSeverity.ERROR, translateFn('Authentication failed. Wrong password.'));
          return false;
        }
      },
      null,
      false
    );
  }

  public async getDataForView(): Promise<AccountLoginSecurityData> {
    return this.component.getViewData();
  }
}
